import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Copy, CheckCheck, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';
import { trackGoogleAdsConversion } from '@/lib/google-ads';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState({ email: false, password: false });

  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }
    let active = true;
    let trackedCompletion = false;
    const maybeTrackConversion = (data) => {
      if (trackedCompletion) return;
      if (data?.status === 'completed' || data?.status === 'pending_asaas') {
        trackedCompletion = true;
        trackEvent('purchase', {
          plan: data.plan_id || null,
          value: Number(data.amount || 0),
          currency: 'BRL',
          transaction_id: sessionId,
          status: data.status,
        });
        trackGoogleAdsConversion('purchase', {
          value: Number(data.amount || 0),
          currency: 'BRL',
          transaction_id: sessionId,
        });
      }
    };
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/checkout/session/${sessionId}`);
        if (active) {
          setSessionData(res.data);
          if (res.data.status === 'completed' || res.data.status === 'pending_asaas') {
            maybeTrackConversion(res.data);
            setLoading(false);
            clearInterval(poll);
          }
        }
      } catch {
        if (active) { setError(true); setLoading(false); clearInterval(poll); }
      }
    }, 3000);
    // Initial fetch
    (async () => {
      try {
        const res = await axios.get(`${API}/checkout/session/${sessionId}`);
        if (active) {
          setSessionData(res.data);
          if (res.data.status === 'completed' || res.data.status === 'pending_asaas') {
            maybeTrackConversion(res.data);
            setLoading(false); clearInterval(poll);
          }
        }
      } catch { if (active) { setError(true); setLoading(false); clearInterval(poll); } }
    })();
    return () => { active = false; clearInterval(poll); };
  }, [sessionId]);

  const copyText = (text, field) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(p => ({ ...p, [field]: true }));
      toast.success('Copiado!');
      setTimeout(() => setCopied(p => ({ ...p, [field]: false })), 3000);
    });
  };

  if (!sessionId || error) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <p className="text-slate-600 mb-4">Sessão não encontrada.</p>
            <Link to="/"><Button className="btn-primary">Voltar à página inicial</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading || sessionData?.status === 'pending') {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#C5A059] animate-spin mx-auto mb-6" />
          <h2 className="text-xl font-bold text-white mb-2">Confirmando seu pagamento...</h2>
          <p className="text-slate-400 text-sm">Sua conta será criada automaticamente assim que o pagamento for confirmado.</p>
        </div>
      </div>
    );
  }

  const creds = sessionData?.credentials;

  return (
    <div className="min-h-screen bg-[#0A2647] relative flex items-center justify-center p-4" data-testid="thank-you-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] bg-[#C5A059]/[0.04] rounded-full blur-[150px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-[#C5A059]/[0.06] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Logo" className="h-16 w-auto mx-auto mb-4" />
        </div>

        <Card className="shadow-2xl border-0 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 py-6 px-6 text-center">
            <CheckCircle2 className="w-14 h-14 text-white mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-white mb-1" data-testid="thank-you-title">Pagamento Confirmado!</h1>
            <p className="text-emerald-100 text-sm">Plano {sessionData?.plan_name} ativado com sucesso</p>
          </div>

          <CardContent className="pt-6 pb-8 space-y-6">
            <div className="text-center">
              <p className="text-slate-500 text-sm mb-1">Empresa:</p>
              <p className="text-lg font-bold text-[#0A2647]" data-testid="thank-you-company">{sessionData?.company_name}</p>
            </div>

            {creds && (
              <div className="bg-[#F8FAFC] rounded-xl p-5 space-y-4 border border-slate-200">
                <p className="text-sm font-semibold text-[#0A2647] text-center">Suas Credenciais de Acesso</p>

                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">E-mail</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white rounded-lg px-3 py-2.5 border border-slate-200 text-sm font-mono text-slate-800" data-testid="thank-you-email">{creds.email}</div>
                    <Button variant="outline" size="sm" onClick={() => copyText(creds.email, 'email')} className="shrink-0 h-10 w-10 p-0" data-testid="copy-email-btn">
                      {copied.email ? <CheckCheck className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Senha Temporária</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white rounded-lg px-3 py-2.5 border border-slate-200 text-sm font-mono font-bold text-[#0A2647]" data-testid="thank-you-password">{creds.password}</div>
                    <Button variant="outline" size="sm" onClick={() => copyText(creds.password, 'password')} className="shrink-0 h-10 w-10 p-0" data-testid="copy-password-btn">
                      {copied.password ? <CheckCheck className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-amber-600 text-center">Recomendamos alterar sua senha após o primeiro acesso.</p>
              </div>
            )}

            {creds?.slug && (
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-1">Sua página de avaliações:</p>
                <p className="text-sm font-mono font-semibold text-[#C5A059]" data-testid="thank-you-slug">falocomodono.com.br/{creds.slug}</p>
              </div>
            )}

            <Link to="/login" className="block">
              <Button className="w-full h-12 bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-bold text-base rounded-xl gap-2" data-testid="thank-you-login-btn">
                Acessar seu Dashboard
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ThankYou;
